import {
  MCPEvaluation,
  MCPTask,
  MCPModel,
  EvaluationRequest,
  EvaluationResponse,
  TaskCreateRequest,
  TaskUpdateRequest,
  EvaluationFilters,
  TaskFilters,
  DashboardStats,
  TaskExecution,
  TaskTreeResponse,
  PaginatedTasksResponse,
} from '../types';

const API_BASE_URL =
  (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000/api/v1';

class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'APIError';
  }
}

// Helper para obtener el token actual de autenticación
const getAuthToken = (): string | null => {
  // En un hook real, esto se obtendría del contexto de autenticación
  // Por ahora, vamos a intentar obtenerlo del localStorage o context
  try {
    const authDataStr = localStorage.getItem('clerk-auth-state');
    if (authDataStr) {
      const authData = JSON.parse(authDataStr);
      return authData.token;
    }
  } catch {
    // Silently fail if no token found
  }
  return null;
};

// Configuración de headers con autenticación
const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

class MCPApiService {
  // Setter para el token (para uso externo)
  setAuthToken(token: string | null) {
    if (token) {
      localStorage.setItem('clerk-auth-state', JSON.stringify({ token }));
    } else {
      localStorage.removeItem('clerk-auth-state');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Si es error 401, el token probablemente expiró
        if (response.status === 401) {
          console.warn(
            '🔑 Token expirado o inválido, limpiando autenticación...'
          );
          this.setAuthToken(null);
          // Podrías disparar un evento aquí para que el hook de auth maneje la reautenticación
          window.dispatchEvent(new CustomEvent('auth-token-expired'));
        }

        throw new APIError(
          errorData.message ||
            `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData.code
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(
        error instanceof Error ? error.message : 'Network error occurred'
      );
    }
  }

  // Evaluaciones
  async getEvaluations(
    filters: EvaluationFilters = {},
    page = 1,
    pageSize = 20
  ): Promise<{
    data: MCPEvaluation[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    // Agregar filtros básicos
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.model) params.append('model', filters.model);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);

    // Agregar filtros booleanos
    if (filters.is_liked !== undefined)
      params.append('is_liked', filters.is_liked.toString());
    if (filters.is_training_data !== undefined)
      params.append('is_training_data', filters.is_training_data.toString());
    if (filters.is_auto !== undefined)
      params.append('is_auto', filters.is_auto.toString());

    // Agregar rango de puntuación
    if (filters.scoreRange) {
      params.append('scoreMin', filters.scoreRange[0].toString());
      params.append('scoreMax', filters.scoreRange[1].toString());
    }

    return this.request<any>(`/evaluations?${params}`);
  }

  async createEvaluation(
    request: EvaluationRequest
  ): Promise<EvaluationResponse> {
    return this.request<EvaluationResponse>('/mcp/evaluate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getEvaluationById(id: string): Promise<MCPEvaluation> {
    return this.request<MCPEvaluation>(`/evaluations/${id}`);
  }

  async deleteEvaluation(id: string): Promise<void> {
    await this.request(`/evaluations/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleLikeEvaluation(id: string): Promise<MCPEvaluation> {
    const response = await this.request<{ evaluation: MCPEvaluation }>(
      `/evaluations/${id}/toggle-like`,
      {
        method: 'POST',
      }
    );
    return response.evaluation;
  }

  async toggleTrainingData(id: string): Promise<MCPEvaluation> {
    const response = await this.request<{ evaluation: MCPEvaluation }>(
      `/evaluations/${id}/toggle-training`,
      {
        method: 'POST',
      }
    );
    return response.evaluation;
  }

  // Tasks
  async getTasks(filters: TaskFilters = {}): Promise<MCPTask[]> {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) => value != null && value !== 'Todos'
        )
      )
    );

    const response = await this.request<PaginatedTasksResponse>(
      `/tasks?${params}`
    );

    console.log('📊 API Response estructura:', {
      hasTasksProperty: 'tasks' in response,
      hasPaginationInfo: 'page' in response && 'total' in response,
      tasksLength: response.tasks?.length || 0,
      response: response,
    });

    return response.tasks || [];
  }

  async createTask(task: TaskCreateRequest): Promise<MCPTask> {
    return this.request<MCPTask>('/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    });
  }

  async updateTask(task: TaskUpdateRequest): Promise<MCPTask> {
    return this.request<MCPTask>(`/tasks/${task.id}`, {
      method: 'PUT',
      body: JSON.stringify(task),
    });
  }

  async deleteTask(id: string): Promise<void> {
    await this.request(`/tasks/${id}`, {
      method: 'DELETE',
    });
  }

  async executeTask(id: string, input: string): Promise<TaskExecution> {
    return this.request<TaskExecution>(`/tasks/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify({ input }),
    });
  }

  async getTaskExecutions(taskId: string): Promise<TaskExecution[]> {
    const response = await this.request<{ executions: TaskExecution[] }>(
      `/tasks/${taskId}/executions`
    );
    return response.executions || [];
  }

  // Nuevos métodos para el TasksTab
  async getTaskTree(): Promise<TaskTreeResponse> {
    return this.request<TaskTreeResponse>('/categories/tree');
  }

  async getTaskCategories(): Promise<{ categories: string[] }> {
    const response =
      await this.request<
        Array<{ id: number; name: string; parent_id: number | null }>
      >('/categories');
    return { categories: response.map(cat => cat.name) };
  }

  async getTask(id: string, includeChildren = false): Promise<MCPTask> {
    const params = includeChildren ? '?include_children=true' : '';
    return this.request<MCPTask>(`/tasks/${id}${params}`);
  }

  // Modelos
  async getModels(): Promise<MCPModel[]> {
    const response = await this.request<{ models: MCPModel[] }>(
      '/models?include_tasks=true&include_categories=true'
    );
    return response.models || [];
  }

  async getModelById(id: string): Promise<MCPModel> {
    return this.request<MCPModel>(
      `/models/${id}?include_tasks=true&include_categories=true`
    );
  }

  async createModel(
    model: Omit<MCPModel, 'id' | 'progress'>
  ): Promise<MCPModel> {
    return this.request<MCPModel>('/models', {
      method: 'POST',
      body: JSON.stringify(model),
    });
  }

  async updateModel(id: string, updates: Partial<MCPModel>): Promise<MCPModel> {
    return this.request<MCPModel>(`/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteModel(id: string): Promise<void> {
    await this.request(`/models/${id}`, {
      method: 'DELETE',
    });
  }

  async deployModel(id: string): Promise<MCPModel> {
    return this.request<MCPModel>(`/models/${id}/deploy`, {
      method: 'POST',
    });
  }

  // Dashboard stats
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('/dashboard/stats');
  }

  // Health check
  async healthCheck(): Promise<{ status: string; mcp: boolean }> {
    return this.request<{ status: string; mcp: boolean }>('/health/mcp');
  }

  // MCP Tools
  async getMCPTools(): Promise<{ tools: any[]; count: number }> {
    return this.request<{ tools: any[]; count: number }>('/tools');
  }

  // Exportar datos
  async exportEvaluations(filters: EvaluationFilters = {}): Promise<Blob> {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value != null)
      )
    );

    const response = await fetch(
      `${API_BASE_URL}/evaluations/export?${params}`,
      {
        headers: {
          Accept: 'text/csv',
        },
      }
    );

    if (!response.ok) {
      throw new APIError(
        `Failed to export evaluations: ${response.statusText}`
      );
    }

    return response.blob();
  }
}

export const apiService = new MCPApiService();
export { APIError };
